import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import MarketingShell, { MK } from "./MarketingShell";

/**
 * Contact Us — public page for new businesses, sales, partnerships and
 * general questions. Matches the marketing.html dark theme (Sora display /
 * Inter body / #2e7dff accent). Existing customers are pointed to /support.
 */
export default function ContactUs() {
  const [form, setForm] = useState({
    your_name: "",
    business_name: "",
    email: "",
    mobile: "",
    comment: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Contact Us — Ratezy";
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.your_name.trim() || !form.business_name.trim() || !form.email.trim() || !form.comment.trim()) {
      setError("Please fill in name, business, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/inquiries", { ...form, source: "contact" });
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MarketingShell active="contact">
      <section className="contact-section" style={{ padding: "clamp(32px, 6vw, 56px) 0 clamp(32px, 6vw, 56px)" }}>
        <div style={{ ...MK.wrap, display: "grid", gap: 22, gridTemplateColumns: "1fr", maxWidth: 820, margin: "0 auto" }} className="contact-grid">
          <div style={{ textAlign: "center" }}>
            <div style={MK.eyebrow}>Contact us</div>
            <h1 style={{ ...MK.h1, fontSize: "clamp(1.8rem, 4vw, 2.6rem)", marginTop: 12 }}>Let's talk about your business.</h1>
            <p style={{ ...MK.lead, maxWidth: 620, margin: "10px auto 0", fontSize: "clamp(0.95rem, 1.2vw, 1rem)" }}>
              New to Ratezy, exploring pricing, or looking to partner with us? Drop us a note
              and we'll get back within one business day.
            </p>
          </div>

          {sent ? (
            <div style={{ ...MK.card, textAlign: "center", padding: 32 }} data-testid="contact-sent">
              <div style={{
                width: 48, height: 48, borderRadius: 999, margin: "0 auto 14px",
                background: "rgba(55,201,131,0.14)", border: "1px solid rgba(55,201,131,0.4)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#37c983" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
              </div>
              <h2 style={{ fontFamily: MK.fonts.display, fontSize: "1.5rem", marginBottom: 8 }}>Message sent.</h2>
              <p style={{ color: MK.color.grey400 }}>Thanks — we'll be in touch shortly.</p>
              <button
                onClick={() => { setSent(false); setForm({ your_name: "", business_name: "", email: "", mobile: "", comment: "" }); }}
                data-testid="contact-send-another"
                style={{ ...MK.btnGhost, marginTop: 18 }}
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} style={{ ...MK.card, padding: "clamp(20px, 2.4vw, 26px)" }} data-testid="contact-form">
              <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
                <Field label="Name" required>
                  <input value={form.your_name} onChange={(e) => set("your_name", e.target.value)} data-testid="contact-name" style={MK.input} />
                </Field>
                <Field label="Business name" required>
                  <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} data-testid="contact-business" style={MK.input} />
                </Field>
                <Field label="Email" required>
                  <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" style={MK.input} />
                </Field>
                <Field label="Phone" hint="Optional">
                  <input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} data-testid="contact-phone" style={MK.input} />
                </Field>
              </div>
              <div style={{ marginTop: 14 }}>
                <Field label="Message" required>
                  <textarea rows={3} value={form.comment} onChange={(e) => set("comment", e.target.value)} data-testid="contact-message"
                    style={{ ...MK.input, resize: "vertical", minHeight: 84, fontFamily: MK.fonts.body }} />
                </Field>
              </div>
              {error && (
                <div style={{ marginTop: 10, color: "#ff8b8b", fontSize: "0.88rem" }} data-testid="contact-error">{error}</div>
              )}
              <button type="submit" disabled={submitting} data-testid="contact-submit"
                style={{ ...MK.btnPrimary, marginTop: 16, width: "100%", opacity: submitting ? 0.6 : 1, cursor: submitting ? "wait" : "pointer" }}>
                {submitting ? "Sending..." : "Send Enquiry"}
              </button>
            </form>
          )}

          {/* Existing business? */}
          <div style={{
            ...MK.card,
            padding: "clamp(16px, 1.8vw, 20px) clamp(20px, 2.4vw, 28px)",
            borderStyle: "dashed",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 16, flexWrap: "wrap",
          }} data-testid="contact-support-callout">
            <div>
              <div style={{ fontFamily: MK.fonts.display, fontWeight: 600, fontSize: "1rem", color: MK.color.white }}>
                Already using Ratezy?
              </div>
              <p style={{ color: MK.color.grey400, marginTop: 2, fontSize: "0.9rem" }}>
                Need help with your account, QR stand, billing, setup or a technical issue?
              </p>
            </div>
            <Link to="/support" data-testid="contact-visit-support" style={{ ...MK.btnGhost, textDecoration: "none" }}>
              Visit Support →
            </Link>
          </div>
        </div>
        <style>{`
          /* Desktop: fit heading + form + support callout in one viewport. */
          @media (min-width: 900px) and (min-height: 700px) {
            .contact-section { padding: 40px 0 32px !important; }
            .contact-grid { gap: 18px !important; }
          }
        `}</style>
      </section>
    </MarketingShell>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label style={{ display: "block" }}>
      <div style={{
        fontFamily: MK.fonts.mono, fontSize: "0.7rem", textTransform: "uppercase",
        letterSpacing: "0.06em", color: MK.color.grey500, marginBottom: 8,
      }}>
        {label}{required && <span style={{ color: MK.color.blue400 }}> *</span>}
        {hint && <span style={{ color: MK.color.grey600, textTransform: "none", marginLeft: 8, letterSpacing: 0 }}>· {hint}</span>}
      </div>
      {children}
    </label>
  );
}
